import React, { FC } from 'react';
import { Theme } from '@material-ui/core/styles';

import { useTranslation } from '../../../../hooks/useTranslation';
import { Grid, makeStyles, createStyles, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import colors from '../../../../themes/main-colors';
import { formatDate } from '../../../../util/format';
import PaymentDueIcon from '../../../../static/paymentDue.svg';

import clsx from 'clsx';

const getFontSize = (size: number) => {
  let fontSize = '20px';
  switch (size) {
    case 3:
      fontSize = '18px';
      return fontSize;
    case 4:
      fontSize = '16px';
      return fontSize;
    case 5:
      fontSize = '14px';
      return fontSize;
    case 6:
      fontSize = '12px';
      return fontSize;
    case 7:
      fontSize = '12px';
      return fontSize;
  }
  return fontSize;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    firstInstallmentCircleIcon: {
      background: colors.errorRed,
      width: 112,
      height: 112,
      borderRadius: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    circleIcon: {
      background: colors.gray97,
      width: 54,
      height: 54,
      borderRadius: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: `2px solid ${colors.gray93}`,
    },
    firstInstallmentCircleText: (props: { largerAmount: number }) => ({
      color: colors.white,
      fontWeight: 700,
      fontSize: props.largerAmount > 2 ? '1.75rem' : '2.125rem',
    }),
    circleText: (props: {
      largerAmount: number;
      isTPAPaymentOverDue: Boolean | undefined;
      isTPALandingPage: Boolean | undefined;
      isPastDate: Boolean | undefined;
    }) => ({
      color:
        props.isTPAPaymentOverDue && props.isPastDate
          ? colors.darkerRed
          : colors.darkGray2,
      fontSize: getFontSize(props.largerAmount),
    }),
    firstInstallmentCircleFooter: {
      color: colors.noirBlur,
      fontWeight: 'bold',
    },
    circleFooter: {
      color: colors.noirBlur,
    },
    delayedPayment: {
      color: colors.darkerRed,
    },
    footerText: {
      maxWidth: '50px',
      display: 'flex',
      fontSize: '14px',
      justifyContent: 'center',
      textAlign: 'center',
    },
    circleUpcomingPayment: (props: {
      isTPALandingPage: Boolean | undefined;
    }) => ({
      border: `2px solid ${colors.darkerRed}`,
      backgroundColor: colors.white,
      marginTop: props.isTPALandingPage ? '4px' : 0,
    }),
    container: (props: { isTPALandingPage: Boolean | undefined }) => ({
      display: 'inline-flex',
      alignItems: 'center',
      flexDirection: 'column',
      margin: theme.spacing(0, 1.5, 3, 1.5),
      '&:first-child': {
        float: 'left',
        [theme.breakpoints.down('sm')]: {
          borderRight: 0,
          padding: props.isTPALandingPage ? 0 : theme.spacing(0, 2.25, 0, 0),
        },
      },
    }),

    completePageContainer: {
      margin: theme.spacing(0, 0, 3, 1.5),
      width: '90px',
      '&:first-child': {
        float: 'none',
        padding: theme.spacing(0),
      },
      '& .completePageCircleIcon': {
        margin: '0 auto 10px',
      },
      '& .paymentDoneCheckmark': {
        backgroundColor: colors.white,
      },
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(0, 0, 3, 0.75),
      },
    },
  }),
);
type Props = {
  installmentAmount: number;
  installmentDate: Date;
  isFirstInstallment: Boolean;
  isPaymentCompleted: Boolean;
  isTPACompletePaymentPage?: Boolean;
  isUpcomingPayment?: Boolean;
  isTPAPaymentOverDue?: Boolean;
  overDueAmount?: number;
  withDownPaymentAmount?: number | Boolean;
  isTPALandingPage?: Boolean;
  isMonthOfInstallment?: Boolean;
  isRollUp?: Boolean;
  isDownPayment?: Boolean;
  isOverDue?: Boolean;
  isOverDueRollAmount?: number;
  isPastDate?: Boolean;
  isAnyFuturePaymentCompleted?: Boolean;
};
const SingleInstallment: FC<Props> = (props: Props) => {
  const {
    installmentAmount,
    installmentDate,
    isFirstInstallment,
    isTPACompletePaymentPage,
    isPaymentCompleted,
    isUpcomingPayment,
    isTPAPaymentOverDue,
    overDueAmount,
    isTPALandingPage,
    isMonthOfInstallment,
    withDownPaymentAmount,
    isRollUp,
    isDownPayment,
    isOverDue,
    isOverDueRollAmount,
    isPastDate,
    isAnyFuturePaymentCompleted
  } = props;
  let largerAmount = 0;
  largerAmount =
    typeof withDownPaymentAmount === 'number'
      ? Number(withDownPaymentAmount.toFixed(2))?.toString().length ?? 0
      : isOverDue
      ? isOverDueRollAmount?.toString().length ?? 0
      : overDueAmount && isUpcomingPayment && isRollUp
      ? overDueAmount?.toString().length ?? 0
      : installmentAmount?.toString().length ?? 0;
  const { t } = useTranslation();
  const classes = useStyles({
    largerAmount,
    isTPAPaymentOverDue: isTPAPaymentOverDue,
    isTPALandingPage,
    isPastDate,
  });
  return (
    <>
      <Grid
        className={`${classes.container} ${
          isTPACompletePaymentPage ? classes.completePageContainer : ''
        }`}
      >
        {(isTPAPaymentOverDue && !isUpcomingPayment && !isAnyFuturePaymentCompleted) ||
        (isMonthOfInstallment && !isDownPayment && !isPaymentCompleted && !isAnyFuturePaymentCompleted) ? (
          <PaymentDueIcon />
        ) : (
          <Typography
            component="div"
            className={clsx(
              isFirstInstallment && !isTPALandingPage
                ? classes.firstInstallmentCircleIcon
                : classes.circleIcon,
              isTPACompletePaymentPage && 'completePageCircleIcon',
              isPaymentCompleted && 'paymentDoneCheckmark',
              isTPAPaymentOverDue &&
                isPastDate && !isAnyFuturePaymentCompleted &&
                classes.circleUpcomingPayment,
            )}
          >
            {isPaymentCompleted ? (
              <CheckIcon
                fontSize="large"
                style={{ color: colors.shoreGreen }}
              />
            ) : (
              <Typography
                component="span"
                variant="h3"
                className={clsx(
                  isFirstInstallment
                    ? classes.firstInstallmentCircleText
                    : classes.circleText,
                )}
              >
                {withDownPaymentAmount
                  ? `$${Number(
                      typeof withDownPaymentAmount === 'number'
                        ? withDownPaymentAmount.toFixed(2)
                        : withDownPaymentAmount,
                    )}`
                  : isOverDue
                  ? `$${isOverDueRollAmount}`
                  : overDueAmount && isUpcomingPayment && isRollUp
                  ? `$${overDueAmount}`
                  : overDueAmount && isUpcomingPayment
                  ? `$${overDueAmount}`
                  : `$${installmentAmount}`}
                {isFirstInstallment && (
                  <sup data-testid={'sup-data-element'}>*</sup>
                )}
              </Typography>
            )}
          </Typography>
        )}

        {isFirstInstallment && (
          <Typography
            data-testid={'due-by-text'}
            component="p"
            variant={'h6'}
            className={classes.firstInstallmentCircleFooter}
          >
            {t('DUE_BY')}
          </Typography>
        )}
        <Typography
          data-testid={'display-date'}
          component="p"
          variant={'h4'}
          className={clsx(
            isFirstInstallment
              ? classes.firstInstallmentCircleFooter
              : classes.circleFooter,
            isTPAPaymentOverDue && isPastDate && !isAnyFuturePaymentCompleted && classes.delayedPayment,
            isMonthOfInstallment && classes.footerText,
          )}
        >
          {installmentDate &&
          ((isFirstInstallment && !isTPALandingPage) ||
            (isTPACompletePaymentPage &&
              isPaymentCompleted &&
              !isTPALandingPage) ||
            (isTPAPaymentOverDue && !isTPALandingPage))
            ? formatDate(installmentDate)
            : isMonthOfInstallment
            ? t('DOWN_PAYMENT')
            : formatDate(installmentDate, 'MMM')}
        </Typography>
      </Grid>
    </>
  );
};
export default SingleInstallment;
